/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 25/09/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 01/03/2026 | Lic. José Luis Aragón Cervantes  | Ajustes en lógica de cobranza y validación de total efectivo y cambio
*      3       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Agregar ayuda visual de monto faltante en cobro
*      4       | 16/06/2026 | Lic. José Luis Aragón Cervantes  | Refactor useReducer: rendimiento en escritura, elimina useMemo/useCallback innecesarios
* --------------------------------------------------------------------------------------- 
*      5       | 26/06/2026 | Lic. Jose Luis Aragon Cervantes  | Integracion autonoma con Venta Super portatil
*      6       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Redondeo de liquidación a cuatro decimales
*/

import TextField from "../../ui/TextField";
import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  AttachMoney,
  CreditCard,
  AccountBalance,
} from "@mui/icons-material";

import { Box, MenuItem, Typography, Alert, Button } from "@mui/material";

import { NumberFormat$ } from "../../../settings/utils";
import { useVentas } from "../../../context/Ventas/VentasContext";
import SizeSoftXModalPortatil from "../leyout/SizeSoftXModalPortatil";

const PAYMENT_TYPES = [
  "EFECTIVO",
  "TRANSFERENCIA",
  "TARJETA DÉBITO",
  "TARJETA CRÉDITO",
  "CRÉDITO A CLIENTE",
];



const DECIMALES_LIQUIDACION = 4;
const FACTOR_LIQUIDACION = 10 ** DECIMALES_LIQUIDACION;
const roundLiquidacion = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  return Math.round((amount + Number.EPSILON) * FACTOR_LIQUIDACION) / FACTOR_LIQUIDACION;
};

const getSafeSummary = (montosVenta) => ({
  subTotal: roundLiquidacion(montosVenta?.subTotal),
  totalDescuento: roundLiquidacion(montosVenta?.totalDescuento),
  totalIVA: roundLiquidacion(montosVenta?.totalIVA),
  total: roundLiquidacion(montosVenta?.total),
  montoEnLetra: montosVenta?.montoEnLetra || "",
});

const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/,/g, "").trim();
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
};

const onlyDigits = (value, maxLength = 4) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxLength);

const initialState = {
  tipoPago: "EFECTIVO",
  montoEfectivo: "",
  montoOtro: "",
  digitosTarjeta: "",
  datosTransferencia: "",
  datosCreditoCliente: "",
  montoEfectivoNum: 0,
  montoOtroNum: 0,
  totalIngresado: 0,
  faltante: 0,
  cambio: 0,
  montoCambioLetra: "CERO PESOS",
};

function cobroReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD': {
      const montoEfectivo = action.field === 'montoEfectivo' ? String(action.value) : state.montoEfectivo;
      const montoOtro = action.field === 'montoOtro' ? String(action.value) : state.montoOtro;
      const tipoPago = action.field === 'tipoPago' ? String(action.value) : state.tipoPago;

      const montoOtroNum = roundLiquidacion(parseAmount(montoOtro));
      const total = roundLiquidacion(action.summaryTotal);

      // When user types a non-cash amount, auto-fill montoEfectivo to cover remaining
      const montoEfectivoNum = roundLiquidacion(action.field === 'montoOtro'
        ? Math.max(total - montoOtroNum, 0)
        : parseAmount(montoEfectivo));
      const montoEfectivoFinal = action.field === 'montoOtro'
        ? String(montoEfectivoNum)
        : montoEfectivo;

      const totalIngresado = roundLiquidacion(montoEfectivoNum + montoOtroNum);
      const cambio = roundLiquidacion(Math.max(totalIngresado - total, 0));
      const faltante = roundLiquidacion(Math.max(total - totalIngresado, 0));

      return {
        ...state,
        tipoPago,
        montoEfectivo: montoEfectivoFinal,
        montoOtro,
        montoEfectivoNum,
        montoOtroNum,
        totalIngresado,
        faltante,
        cambio,
      };
    }

    case 'SET_DIGITOS_TARJETA':
      return { ...state, digitosTarjeta: onlyDigits(action.value, 4) };

    case 'CHANGE_PAYMENT_TYPE': {
      const total = roundLiquidacion(action.total);
      if (action.tipoPago === "EFECTIVO" && total > 0) {
        const montoEfectivoStr = String(total);
        return {
          ...initialState,
          tipoPago: "EFECTIVO",
          montoEfectivo: montoEfectivoStr,
          montoEfectivoNum: total,
          totalIngresado: total,
          faltante: 0,
          cambio: 0,
          montoCambioLetra: state.montoCambioLetra,
        };
      }
      return { ...initialState, tipoPago: action.tipoPago, montoCambioLetra: state.montoCambioLetra };
    }

    case 'SET_CAMBIO_LETRA':
      return { ...state, montoCambioLetra: action.value };

    case 'RESET': {
      const total = roundLiquidacion(action.total);
      const montoEfectivoStr = total > 0 ? String(total) : "";
      return {
        ...initialState,
        tipoPago: action.tipoPago || "EFECTIVO",
        montoEfectivo: montoEfectivoStr,
        montoEfectivoNum: total,
        totalIngresado: total,
        faltante: 0,
        cambio: 0,
      };
    }

    default:
      return state;
  }
}

function validateBeforeCharge(state, summary) {
  if (summary.total <= 0) return "No hay total de venta válido para cobrar.";

  if (state.tipoPago === "EFECTIVO") {
    if (state.montoEfectivoNum <= 0) return "Ingrese el monto en efectivo.";
    if (state.montoEfectivoNum < summary.total) return "El efectivo es insuficiente para cubrir la venta.";
    return "";
  }

  if (state.montoOtroNum <= 0) return "Ingrese el monto del tipo de pago.";

  if (state.totalIngresado < summary.total) return "La suma de montos no cubre el total de la venta.";

  const isCard = state.tipoPago === "TARJETA DÉBITO" || state.tipoPago === "TARJETA CRÉDITO";
  if (isCard && state.digitosTarjeta.trim() !== "" && state.digitosTarjeta.length !== 4)
    return "Ingrese los 4 dígitos de la tarjeta.";

  if (state.tipoPago === "TRANSFERENCIA" && state.datosTransferencia.trim() !== "" && !state.datosTransferencia.trim())
    return "Ingrese los datos de la transferencia.";

  if (state.tipoPago === "CRÉDITO A CLIENTE" && state.datosCreditoCliente.trim() !== "" && !state.datosCreditoCliente.trim())
    return "Ingrese los datos del crédito a cliente.";

  return "";
}

function buildPayload(state, summary) {
  const isCard = state.tipoPago === "TARJETA DÉBITO" || state.tipoPago === "TARJETA CRÉDITO";
  const isTransferencia = state.tipoPago === "TRANSFERENCIA";
  const isCredito = state.tipoPago === "CRÉDITO A CLIENTE";

  const referencia = isCredito
    ? state.datosCreditoCliente.trim()
    : isTransferencia
      ? state.datosTransferencia.trim()
      : isCard
        ? state.digitosTarjeta.trim()
        : "";

  return {
    foliocortecaja: 0,
    remision: 0,
    pc: "",
    usuario: "",
    clavecliente: "",
    razonsocial: "",
    fechaventa: "",
    horaventa: "",
    factura: "0",
    facturaelectronica: "",
    condicionesdepago: "",
    tipopago: state.tipoPago.trim(),
    serialtarjetachequevale: referencia,
    totaltipopago: roundLiquidacion(state.montoOtroNum),
    totalefectivo: roundLiquidacion(state.montoEfectivoNum),
    totaldescuento: roundLiquidacion(summary.totalDescuento),
    totalcambio: roundLiquidacion(state.cambio),
    numeroautorizacion: isCard ? state.digitosTarjeta.trim() : "",
    tipodebitocredito: state.tipoPago,
    banco: "",
    subtotal: roundLiquidacion(summary.subTotal),
    iva: roundLiquidacion(summary.totalIVA),
    total: roundLiquidacion(summary.total),
    totalenletra: summary.montoEnLetra,
  };
}

export default function ModalCobroVentas(props) {
  const {
    openModalCobrarVentas,
    montosVenta,
    handleCloseModalCobroVentas,
    handleCobrar,
  } = props;

  const { MontoEnLetra } = useVentas();

  const [internalOpen, setInternalOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const [state, dispatch] = useReducer(cobroReducer, initialState);
  const [errorMensaje, setErrorMensaje] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const logRef = useRef([]);
  const mountedRef = useRef(false);

  const refTipoPago = useRef(null);
  const refMontoEfectivo = useRef(null);
  const refMontoOtro = useRef(null);
  const refDigitos = useRef(null);
  const refTransferencia = useRef(null);
  const refCreditoCliente = useRef(null);

  const summary = useMemo(() => getSafeSummary(montosVenta), [montosVenta]);

  const isCardPayment = state.tipoPago === "TARJETA DÉBITO" || state.tipoPago === "TARJETA CRÉDITO";
  const requiresMontoOtro = state.tipoPago !== "EFECTIVO";
  const requiresReferenciaTarjeta = isCardPayment;
  const requiresReferenciaTransferencia = state.tipoPago === "TRANSFERENCIA";
  const requiresReferenciaCredito = state.tipoPago === "CRÉDITO A CLIENTE";

  const canCharge = !isProcessing && summary.total > 0 && (
    state.tipoPago === "EFECTIVO"
      ? state.montoEfectivoNum >= summary.total && state.montoEfectivoNum > 0
      : state.totalIngresado >= summary.total
  );

  const canChargeRef = useRef(false);
  canChargeRef.current = canCharge;
  const isProcessingRef = useRef(false);
  isProcessingRef.current = isProcessing;

  const writeInternalLog = useCallback((level, message, extra = null) => {
    const entry = { time: new Date().toISOString(), level, message, extra };
    logRef.current.push(entry);
    if (logRef.current.length > 300) logRef.current = logRef.current.slice(-300);
    if (typeof window !== "undefined") window.__modalCobroVentasLog = logRef.current;
    if (level === "error") console.error("[ModalCobroVentas]", message, extra ?? "");
  }, []);

  const focusInputByPaymentType = useCallback((tipoPago) => {
    setTimeout(() => {
      if (tipoPago === "EFECTIVO") {
        refMontoEfectivo.current?.focus?.();
        refMontoEfectivo.current?.select?.();
        return;
      }
      if (tipoPago === "TRANSFERENCIA") {
        refTransferencia.current?.focus?.();
        return;
      }
      if (tipoPago === "CRÉDITO A CLIENTE") {
        refCreditoCliente.current?.focus?.();
        return;
      }
      if (tipoPago === "TARJETA DÉBITO" || tipoPago === "TARJETA CRÉDITO") {
        refMontoOtro.current?.focus?.();
        refMontoOtro.current?.select?.();
      }
    }, 120);
  }, []);

  const handleInputChange = (field, value) => {
    dispatch({ type: 'SET_FIELD', field, value, summaryTotal: summary.total });
  };

  const handleChangeTipoPago = useCallback((tipoPago) => {
    dispatch({ type: 'CHANGE_PAYMENT_TYPE', tipoPago, total: summary.total });
    setErrorMensaje("");
    writeInternalLog("info", `Tipo de pago cambiado a ${tipoPago}`);
    focusInputByPaymentType(tipoPago);
  }, [writeInternalLog, focusInputByPaymentType, summary.total]);

  const stateRef = useRef(state);
  stateRef.current = state;
  const summaryRef = useRef(summary);
  summaryRef.current = summary;

  const CobrarVenta = useCallback(async () => {
    if (isProcessingRef.current) return;

    const currentState = stateRef.current;
    const currentSummary = summaryRef.current;
    const validationError = validateBeforeCharge(currentState, currentSummary);

    if (validationError) {
      setErrorMensaje(validationError);
      writeInternalLog("error", validationError, { state: currentState, summary: currentSummary });
      return;
    }

    setErrorMensaje("");
    setIsProcessing(true);
    try {
      await handleCobrar(buildPayload(currentState, currentSummary));
      writeInternalLog("info", "Cobro ejecutado correctamente");
    } catch (error) {
      const message = error?.message || "Ocurrió un error al cobrar la venta.";
      setErrorMensaje(message);
      writeInternalLog("error", message, error);
    } finally {
      setIsProcessing(false);
    }
  }, [handleCobrar, writeInternalLog]);

  useEffect(() => {
    let timerOpen;
    let timerVisible;

    if (openModalCobrarVentas) {
      setInternalOpen(true);
      dispatch({ type: 'RESET', tipoPago: montosVenta?.tipoPago || "EFECTIVO", total: summary.total });
      setErrorMensaje("");
      setIsProcessing(false);
      logRef.current = [];
      writeInternalLog("info", "Modal abierto", { summary });

      timerOpen = setTimeout(() => {
        setModalVisible(true);
        focusInputByPaymentType(montosVenta?.tipoPago || "EFECTIVO");
      }, 50);
    } else {
      setModalVisible(false);
      timerVisible = setTimeout(() => {
        setInternalOpen(false);
      }, 350);
    }

    return () => {
      if (timerOpen) clearTimeout(timerOpen);
      if (timerVisible) clearTimeout(timerVisible);
    };
  }, [openModalCobrarVentas, montosVenta?.tipoPago, summary, writeInternalLog, focusInputByPaymentType]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    if (state.cambio <= 0) {
      dispatch({ type: 'SET_CAMBIO_LETRA', value: 'CERO PESOS' });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const letra = await MontoEnLetra(state.cambio);
        if (mountedRef.current) {
          dispatch({ type: 'SET_CAMBIO_LETRA', value: letra || 'CERO PESOS' });
        }
      } catch {
        if (mountedRef.current) {
          dispatch({ type: 'SET_CAMBIO_LETRA', value: 'CERO PESOS' });
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [state.cambio]);

  useEffect(() => {
    if (!openModalCobrarVentas) return;

    const onKeyDown = (event) => {
      if (isProcessingRef.current) return;

      if (event.altKey && (event.key === "End" || event.code === "End")) {
        event.preventDefault();
        if (canChargeRef.current) {
          CobrarVenta();
        }
        return;
      }

      if (event.altKey && event.code === "KeyE") {
        event.preventDefault();
        handleChangeTipoPago("EFECTIVO");
        return;
      }

      if (event.altKey && event.code === "KeyC") {
        event.preventDefault();
        handleChangeTipoPago("TARJETA CRÉDITO");
        return;
      }

      if (event.altKey && event.code === "KeyD") {
        event.preventDefault();
        handleChangeTipoPago("TARJETA DÉBITO");
        return;
      }

      if (event.altKey && event.code === "KeyT") {
        event.preventDefault();
        handleChangeTipoPago("TRANSFERENCIA");
        return;
      }

      if (event.altKey && event.code === "F6") {
        event.preventDefault();
        handleChangeTipoPago("CRÉDITO A CLIENTE");
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [openModalCobrarVentas, CobrarVenta, handleChangeTipoPago]);

  return (
    <SizeSoftXModalPortatil
            open={internalOpen}
            onClose={isProcessing ? undefined : handleCloseModalCobroVentas}
        >
        <Fragment>
          {errorMensaje ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMensaje("")}>
              {errorMensaje}
            </Alert>
          ) : null}

          <div className="containerCobroVentasMenu">
            <Tooltip title="Pago en efectivo">
              <IconButton onClick={() => handleChangeTipoPago("EFECTIVO")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <AttachMoney sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+E
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>

            <Tooltip title="Pago con tarjeta crédito">
              <IconButton onClick={() => handleChangeTipoPago("TARJETA CRÉDITO")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <CreditCard sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+C
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>

            <Tooltip title="Pago con tarjeta débito">
              <IconButton onClick={() => handleChangeTipoPago("TARJETA DÉBITO")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <CreditCard sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+D
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>

            <Tooltip title="Pago mediante transferencia">
              <IconButton onClick={() => handleChangeTipoPago("TRANSFERENCIA")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <AccountBalance sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+T
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>

            <Tooltip title="Pago a crédito">
              <IconButton onClick={() => handleChangeTipoPago("CRÉDITO A CLIENTE")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <AccountBalance sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+F6
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
          </div>

          <div className="venta-detalles-container" style={{ textAlign: "center", marginTop: 0, paddingTop: 0 }}>
            <div className="detalle-item" style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "center", gap: "0 1rem" }}>
              <span><span className="detalle-text">Descuento:</span> {NumberFormat$(summary.totalDescuento)}</span>
              <span><span className="detalle-text">Subtotal:</span> {NumberFormat$(summary.subTotal)}</span>
              <span><span className="detalle-text">IVA:</span> {NumberFormat$(summary.totalIVA)}</span>
            </div>
            <br />
            <h1 style={{ margin: "0.25rem 0" }}>{NumberFormat$(summary.total)}</h1>
            <div>
              <span className="detalle-text">Monto en letra:</span> {summary.montoEnLetra}
            </div>
          </div>

          <div className="containerCobroVentas">
            <div className="actionSectionCobroVenta">
              <TextField autoComplete="off"
                select
                inputRef={refTipoPago}
                label="Tipo de pago"
                value={state.tipoPago}
                onChange={(e) => handleChangeTipoPago(String(e.target.value))}
                sx={{ margin: ".5rem .3rem", width: "250px" }}
              >
                {PAYMENT_TYPES.map((x) => (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ))}
              </TextField>

              {requiresReferenciaTarjeta && (
                <TextField autoComplete="off"
                  inputRef={refDigitos}
                  label="4 dígitos tarjeta"
                  value={state.digitosTarjeta}
                  onChange={(e) => dispatch({ type: 'SET_DIGITOS_TARJETA', value: e.target.value })}
                  inputProps={{ maxLength: 4, inputMode: "numeric" }}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}

              {requiresReferenciaTransferencia && (
                <TextField autoComplete="off"
                  inputRef={refTransferencia}
                  label="Datos transferencia"
                  value={state.datosTransferencia}
                  onChange={(e) => handleInputChange("datosTransferencia", e.target.value)}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}

              {requiresReferenciaCredito && (
                <TextField autoComplete="off"
                  inputRef={refCreditoCliente}
                  label="Datos crédito a cliente"
                  value={state.datosCreditoCliente}
                  onChange={(e) => handleInputChange("datosCreditoCliente", e.target.value)}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}

              {requiresMontoOtro && (
                <TextField autoComplete="off"
                  inputRef={refMontoOtro}
                  label="Monto tipo pago"
                  value={state.montoOtro}
                  onChange={(e) => handleInputChange("montoOtro", e.target.value)}
                  type="text"
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}
            </div>

            <div className="actionSectionCobroVenta" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <TextField autoComplete="off"
                inputRef={refMontoEfectivo}
                label="Monto efectivo"
                value={state.montoEfectivo}
                onChange={(e) => handleInputChange("montoEfectivo", e.target.value)}
                type="text"
                inputProps={{ inputMode: "decimal" }}
                sx={{ margin: ".5rem .3rem", width: "460px" }}
              />

              <hr className="separator" style={{ width: "460px" }} />

              <TextField autoComplete="off"
                label="Monto cambio"
                value={`${NumberFormat$(state.cambio)}\n(${state.montoCambioLetra})`}
                multiline
                disabled
                sx={{
                  width: "460px",
                  margin: ".5rem .3rem",
                  backgroundColor: state.faltante > 0 ? "#ff3333" : "white",
                  "& .MuiInputBase-input": {
                    color: state.faltante > 0 ? "white" : "black",
                    fontWeight: "bold",
                  },
                }}
              />

              <Box
                sx={{
                  width: "460px",
                  margin: ".25rem .3rem 0",
                  px: 1.5,
                  py: 0.75,
                  border: "1px solid",
                  borderColor: state.faltante > 0 ? "warning.light" : "success.light",
                  borderRadius: 1,
                  bgcolor: state.faltante > 0 ? "#fff8e1" : "#edf7ed",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: 700,
                    color: state.faltante > 0 ? "warning.dark" : "success.dark",
                  }}
                >
                  <span>{state.faltante > 0 ? "Falta para completar venta" : "Venta cubierta"}</span>
                  <span>{NumberFormat$(state.faltante)}</span>
                </Typography>
              </Box>

              <div style={{ display: "flex", justifyContent: "center", margin: ".5rem .3rem" }}>
                <Button
                  variant="contained"
                  onClick={CobrarVenta}
                  disabled={!canCharge}
                  sx={{ height: 56, minWidth: 180 }}
                >
                  {isProcessing ? "Cobrando..." : "Cobrar venta"}
                </Button>
              </div>
            </div>
          </div>
        </Fragment>
        </SizeSoftXModalPortatil>
  );
}


